import { AlertCircle, Shield } from 'lucide-react';
import { formatBillNaira } from './billPaymentUtils';

export interface BillSummaryRow {
  label: string;
  value: string;
  valueClassName?: string;
}

interface BillPaymentSummaryProps {
  rows: BillSummaryRow[];
  total: number | null;
  walletBalance: number;
  showNotices?: boolean;
  compact?: boolean;
  reversalNotice?: string;
}

export function BillPaymentSummary({
  rows,
  total,
  walletBalance,
  showNotices = true,
  compact = false,
  reversalNotice,
}: BillPaymentSummaryProps) {
  const totalFormatted = total != null ? formatBillNaira(total) : '—';
  const insufficient = total != null && walletBalance < total;

  return (
    <div className="space-y-4">
      <div className={`space-y-3 ${compact ? 'text-sm' : ''}`}>
        {rows.map((row) => (
          <div key={row.label} className="flex justify-between gap-4 border-b border-gray-50 pb-2 last:border-0">
            <span className="shrink-0 text-gray-500">{row.label}</span>
            <span className={`text-right font-medium text-gray-900 ${row.valueClassName ?? ''}`}>
              {row.value}
            </span>
          </div>
        ))}
        <div className="flex justify-between gap-4 border-b border-gray-50 pb-2">
          <span className="shrink-0 text-gray-500">Wallet</span>
          <span className={`font-medium ${insufficient ? 'text-red-600' : 'text-gray-900'}`}>
            {formatBillNaira(walletBalance)}
          </span>
        </div>
        <div className="flex justify-between gap-4 pt-1">
          <span className="text-base font-semibold text-gray-900">Total</span>
          <span className="text-base font-bold text-brand-600">{totalFormatted}</span>
        </div>
      </div>

      {showNotices && !compact && (
        <>
          <div className="rounded-xl border border-blue-100 bg-blue-50 p-3">
            <div className="flex items-start gap-2">
              <Shield className="mt-0.5 h-4 w-4 shrink-0 text-blue-600" />
              <p className="text-xs text-blue-800">Payments are encrypted and processed securely.</p>
            </div>
          </div>
          {reversalNotice && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
              <div className="flex items-start gap-2">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                <p className="text-xs text-amber-900">{reversalNotice}</p>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
